import { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import TrainingScreen from './components/TrainingScreen/TrainingScreen';
import SharePanel from './components/SharePanel/SharePanel';
import { getPhraseFromURL } from './utils/urlManager';
import './styles/global.css';

function App() {
    const [dark, setDark] = useState(
        () => window.matchMedia('(prefers-color-scheme: dark)').matches
    );
    const toggleRef = useRef(null);
    const [customPhrase, setCustomPhrase] = useState(() => getPhraseFromURL());

    // Inject view-transition CSS directly — PostCSS/CRA strips ::view-transition
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            ::view-transition-old(root),
            ::view-transition-new(root) {
                animation: none;
                mix-blend-mode: normal;
            }
            ::view-transition-new(root) { z-index: 999; }
            ::view-transition-old(root) { z-index: 1; }

            .theme-toggle svg {
                transition: transform 0.6s cubic-bezier(0.4, 0, 0, 1);
            }
            .theme-toggle.spinning svg {
                transform: rotate(360deg) scale(1.2);
            }

            @keyframes ripple {
                0% { transform: translate(-50%, -50%) scale(0); opacity: 0.5; }
                100% { transform: translate(-50%, -50%) scale(1); opacity: 0; }
            }
            .theme-ripple {
                position: fixed;
                width: 40px;
                height: 40px;
                border-radius: 50%;
                pointer-events: none;
                z-index: 99999;
                border: 2px solid var(--c4);
                animation: ripple 0.7s cubic-bezier(0, 0, 0.2, 1) forwards;
            }
        `;
        document.head.appendChild(style);
        return () => style.remove();
    }, []);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
    }, [dark]);

    const handleToggle = (e) => {
        const x = e.clientX;
        const y = e.clientY;
        const endRadius = Math.hypot(
            Math.max(x, window.innerWidth - x),
            Math.max(y, window.innerHeight - y)
        );

        // Spin the icon
        const btn = toggleRef.current;
        btn.classList.add('spinning');
        setTimeout(() => btn.classList.remove('spinning'), 600);

        // Ripple pulse centered on button
        const rect = btn.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const ripple = document.createElement('div');
        ripple.className = 'theme-ripple';
        ripple.style.left = cx + 'px';
        ripple.style.top = cy + 'px';
        document.body.appendChild(ripple);
        setTimeout(() => ripple.remove(), 700);

        if (document.startViewTransition) {
            const transition = document.startViewTransition(() => {
                flushSync(() => setDark(d => !d));
            });

            transition.ready.then(() => {
                // New theme circle expanding
                document.documentElement.animate(
                    {
                        clipPath: [
                            `circle(0px at ${x}px ${y}px)`,
                            `circle(${endRadius}px at ${x}px ${y}px)`
                        ]
                    },
                    {
                        duration: 800,
                        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
                        pseudoElement: '::view-transition-new(root)'
                    }
                );

                // Old theme fades out and scales slightly
                document.documentElement.animate(
                    {
                        opacity: [1, 0.5],
                        transform: ['scale(1)', 'scale(1.03)']
                    },
                    {
                        duration: 800,
                        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
                        pseudoElement: '::view-transition-old(root)'
                    }
                );
            });
        } else {
            setDark(d => !d);
        }
    };

    return (
        <div className="app-container">
            <button
                ref={toggleRef}
                className="theme-toggle"
                onClick={handleToggle}
                aria-label="Переключить тему"
            >
                {dark ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="5"/>
                        <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                        <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
                        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                    </svg>
                ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                    </svg>
                )}
            </button>
            <h1>Тренировка неправильной раскладки</h1>
            <TrainingScreen
                customPhrase={customPhrase}
                onCustomDone={() => {
                    setCustomPhrase(null);
                    window.history.replaceState({}, '', window.location.pathname);
                }}
            />
            {!customPhrase && <SharePanel />}
        </div>
    );
}

export default App;
